/**
 * Utility per gestire l'apertura e chiusura di elementi UI
 * (Accordions, Modal, Drawer, Toggle Checkbox)
 */
class SmoothToggle {
  constructor(trigger, options = {}) {
    this.trigger = trigger;
    this.content = options.content || trigger.nextElementSibling;
    this.type = options.type || 'maxHeight'; // 'maxHeight', 'hidden'
    this.icon = options.icon || trigger.querySelector('svg');
    this.iconActiveClass = options.iconActiveClass || 'rotate-180';
    
    // Configurazione per toggle tramite checkbox (es. Gift message)
    this.isCheckbox = trigger.tagName && trigger.tagName.toLowerCase() === 'input' && trigger.type === 'checkbox';
    this.iconUnchecked = options.iconUnchecked || null;
    this.iconChecked = options.iconChecked || null;
    
    this.isOpen = false;
    
    this.init();
  }
  
  init() {
    if (!this.trigger || !this.content) return;
    
    if (this.isCheckbox) {
      this.trigger.addEventListener('change', () => this.toggleCheckbox());
      this.toggleCheckbox(); // Stato iniziale
    } else {
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    }
  }
  
  toggle() {
    this.isOpen = !this.isOpen;
    
    if (this.type === 'maxHeight') {
      if (this.isOpen) {
        this.content.style.maxHeight = this.content.scrollHeight + 'px';
        if (this.icon) this.icon.classList.add(this.iconActiveClass);
      } else {
        this.content.style.maxHeight = null;
        if (this.icon) this.icon.classList.remove(this.iconActiveClass);
      }
    }
  }
  
  toggleCheckbox() {
    this.isOpen = this.trigger.checked;
    
    if (this.isOpen) {
      this.content.classList.remove('hidden');
      if (this.iconUnchecked) this.iconUnchecked.classList.add('hidden');
      if (this.iconChecked) this.iconChecked.classList.remove('hidden');
    } else {
      this.content.classList.add('hidden');
      if (this.iconUnchecked) this.iconUnchecked.classList.remove('hidden');
      if (this.iconChecked) this.iconChecked.classList.add('hidden');
    }
  }
}

window.SmoothToggle = SmoothToggle;

class FocusTrap {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container;
    this.onEscape = options.onEscape || null;
    this.initialFocus = options.initialFocus || null;
    this.restoreFocus = options.restoreFocus !== false;
    this.previouslyFocused = null;
    this.isActive = false;
    
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  activate() {
    if (this.isActive || !this.container) return;
    this.isActive = true;
    this.previouslyFocused = document.activeElement;
    
    document.addEventListener('keydown', this.handleKeyDown);
    
    // Give the browser a moment to apply 'visibility: visible' or 'display: block'
    setTimeout(() => {
      if (!this.isActive) return;
      if (this.initialFocus && typeof this.initialFocus === 'string') {
        const el = this.container.querySelector(this.initialFocus);
        if (el) el.focus();
      } else if (this.initialFocus instanceof HTMLElement) {
        this.initialFocus.focus();
      } else {
        const firstFocusable = this.getFocusableElements()[0];
        if (firstFocusable) firstFocusable.focus();
        else {
          this.container.setAttribute('tabindex', '-1');
          this.container.focus();
        }
      }
    }, 50);
  }

  deactivate() {
    if (!this.isActive) return;
    this.isActive = false;
    document.removeEventListener('keydown', this.handleKeyDown);
    
    if (this.restoreFocus && this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      // Small timeout to allow drawers/modals to close before restoring focus
      setTimeout(() => {
        this.previouslyFocused.focus();
      }, 10);
    }
  }

  getFocusableElements() {
    return Array.from(
      this.container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => {
      // Check if element is visible
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      if (typeof this.onEscape === 'function') {
        this.onEscape();
      }
      return;
    }

    if (e.key === 'Tab') {
      const focusableElements = this.getFocusableElements();
      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement || document.activeElement === this.container) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }
}

window.FocusTrap = FocusTrap;

/**
 * Fades media in once it has decoded, instead of letting it snap in mid-scroll.
 * Marks the images themselves: the preloader in product.js flips loading from lazy to
 * eager while scrolling, so the attribute cannot be used to drive the style.
 * Images already decoded, and eager ones (the LCP), are left alone.
 */
function fadeInMedia(root) {
  if (!root) return;

  root.querySelectorAll('img:not([loading="eager"])').forEach(img => {
    if (img.dataset.gzFaded) return;
    img.dataset.gzFaded = 'true';

    if (img.complete && img.naturalWidth > 0) return;

    img.classList.add('gz-fade');
    const reveal = () => img.classList.add('is-loaded');
    img.addEventListener('load', reveal, { once: true });
    // a broken image still has to become visible, or it silently disappears
    img.addEventListener('error', reveal, { once: true });
  });
}

window.gzFadeInMedia = fadeInMedia;
